// Team > Invitations (owners and administrators). See ./invitations.ts for the rules.
import { type FormEvent, useEffect, useState, useSyncExternalStore } from 'react';
import { MailPlus } from 'lucide-react';
import { useWorkspace } from '../../state/workspace.tsx';
import { formatAbsolute } from '../../lib/format.ts';
import { Icon } from '../../ui/Icon.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { StatePanel } from '../../ui/StatePanel.tsx';
import { INVITATION_STATES, INVITATION_STATE_COPY, ROLE_LABELS, type InvitationView, type RoleId, delegableRoles, revocable, roleText } from '../../identity/contract.ts';
import { FlowDiagnostics } from '../../identity/IdentityParts.tsx';
import { type InvitationFilter, type InvitationsSnapshot, InvitationsController } from './invitations.ts';

export function InvitationsPanel({ controller: injected }: { controller?: InvitationsController }) {
  const ws = useWorkspace();
  const myRoles = ws.session.memberships.find(m => m.tenant_id === ws.session.tenant?.id)?.role_ids ?? [];
  const [controller] = useState(() => injected ?? new InvitationsController({ api: ws.api, myRoles, hasTotp: ws.session.auth_methods.includes('totp') }));
  const snap = useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
  useEffect(() => {
    if (!injected) void controller.start();
    return () => controller.dispose();
  }, [controller, injected]);

  return (
    <section className="invites" aria-labelledby="invites-title">
      <h2 id="invites-title" className="section-title">Invitations</h2>
      <InvitationsBody snap={snap} controller={controller} myRoles={myRoles} />
    </section>
  );
}

function InvitationsBody({ snap, controller, myRoles }: { snap: InvitationsSnapshot; controller: InvitationsController; myRoles: readonly string[] }) {
  const access = snap.access;
  switch (access.kind) {
    case 'loading':
      return <p className="meta" role="status">Checking invitations…</p>;
    case 'missing':
      return (
        <>
          <StatePanel variant="unconfigured" compact headingLevel={3} title="Invitations aren’t available on this server yet" body="Nothing can be sent or listed until the server supports them." />
          <FlowDiagnostics diagnostic={access.diagnostic} />
        </>
      );
    case 'needs_mfa':
      return <StatePanel variant="denied" compact headingLevel={3} title="Sign in with your verification code to manage invitations" body="Inviting people needs two-step verification on this sign-in. Sign out, then sign in again with your password and a code." />;
    case 'denied':
      return (
        <>
          <StatePanel variant="denied" compact headingLevel={3} title="Your role can’t manage invitations" body="Owners and administrators with two-step verification can invite people." />
          <FlowDiagnostics diagnostic={access.diagnostic} />
        </>
      );
    case 'error':
      return (
        <>
          <StatePanel variant="error" compact headingLevel={3} title="Couldn’t check invitations" action={<button type="button" className="btn" onClick={() => void controller.start()}>Try again</button>} />
          <FlowDiagnostics diagnostic={access.diagnostic} />
        </>
      );
    default:
      return (
        <>
          {access.mail ? <InviteForm snap={snap} controller={controller} myRoles={myRoles} /> : (
            <p className="notice notice-warn" role="status">Invitation email isn’t set up on this server, so new invitations can’t be sent. Existing invitations are listed below.</p>
          )}
          <InvitationList snap={snap} controller={controller} />
        </>
      );
  }
}

function InviteForm({ snap, controller, myRoles }: { snap: InvitationsSnapshot; controller: InvitationsController; myRoles: readonly string[] }) {
  const choices = delegableRoles(myRoles);
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<RoleId[]>(() => (choices.includes('agent') ? ['agent'] : choices.slice(0, 1)));
  const create = snap.create;
  const sending = create.phase === 'sending';
  const edit = <T,>(apply: (v: T) => void) => (v: T) => { apply(v); controller.clearCreate(); };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (sending) return;
    void controller.create(email, roles).then(() => {
      if (controller.getSnapshot().create.phase === 'done') setEmail('');
    });
  };
  const toggle = (role: RoleId, on: boolean) => setRoles(r => (on ? [...r, role] : r.filter(x => x !== role)));
  const result = create.result;
  return (
    <form className="invite-form" onSubmit={submit} noValidate aria-describedby={create.error ? 'invite-create-error' : undefined}>
      <div className="field">
        <label htmlFor="invite-email">Email</label>
        <input id="invite-email" type="email" autoComplete="off" value={email} onChange={e => edit(setEmail)(e.target.value)} />
      </div>
      <fieldset className="radio-row">
        <legend>Role</legend>
        {choices.map(role => (
          <label key={role} className="check">
            <input type="checkbox" checked={roles.includes(role)} onChange={e => edit<boolean>(on => toggle(role, on))(e.target.checked)} />
            {ROLE_LABELS[role]}
          </label>
        ))}
      </fieldset>
      <p className="field-hint">You can give only roles your own role allows. No role includes recordings or transcripts; that access is granted separately.</p>
      {create.error ? (
        <div id="invite-create-error" className="form-error" role="alert">
          {create.error}
          <FlowDiagnostics diagnostic={create.diagnostic} />
        </div>
      ) : null}
      {create.phase === 'done' && result ? (
        <p className="notice notice-info" role="status">
          {result.state === 'queued'
            ? `Invitation for ${result.email} queued. It hasn’t been emailed yet; its state below changes when the email provider takes it, which isn’t proof it reached the inbox.`
            : `Invitation for ${result.email}: ${INVITATION_STATE_COPY[result.state].label}. ${INVITATION_STATE_COPY[result.state].hint}`}
        </p>
      ) : null}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={sending || roles.length === 0} aria-busy={sending || undefined}>
          <Icon icon={MailPlus} /> {sending ? 'Sending…' : create.retrySafe ? 'Send again' : 'Send invitation'}
        </button>
      </div>
    </form>
  );
}

const FILTER_LABELS: Record<InvitationFilter, string> = {
  '': 'All',
  ...Object.fromEntries(INVITATION_STATES.map(s => [s, INVITATION_STATE_COPY[s].label])),
} as Record<InvitationFilter, string>;

function InvitationList({ snap, controller }: { snap: InvitationsSnapshot; controller: InvitationsController }) {
  const [confirm, setConfirm] = useState<InvitationView | null>(null);
  return (
    <div className="invite-list">
      <div className="filters" role="group" aria-label="Filter invitations">
        <label className="filter">
          <span>Show</span>
          <select value={snap.filter} onChange={e => controller.setFilter(e.target.value as InvitationFilter)}>
            {(['', ...INVITATION_STATES] as InvitationFilter[]).map(f => <option key={f} value={f}>{FILTER_LABELS[f]}</option>)}
          </select>
        </label>
        {/* States change on the server (for example when the email provider takes a message). */}
        <button type="button" className="btn btn-small" onClick={() => controller.reload()} disabled={snap.list === 'loading'}>Refresh</button>
      </div>
      {snap.listNotice ? <p className="notice" role="status">{snap.listNotice}</p> : null}
      {snap.list === 'loading' ? <p className="meta" role="status">Loading invitations…</p> : null}
      {snap.list === 'error' ? (
        <>
          <StatePanel variant="error" compact headingLevel={3} title="Couldn’t load invitations" action={<button type="button" className="btn" onClick={() => controller.reload()}>Try again</button>} />
          <FlowDiagnostics diagnostic={snap.listDiagnostic} />
        </>
      ) : null}
      {snap.list !== 'loading' && snap.list !== 'error' && snap.items.length === 0 ? (
        <p className="meta">{snap.filter ? 'No invitations in this state.' : 'No invitations yet.'}</p>
      ) : null}
      {snap.items.length > 0 ? (
        <ul className="invite-rows" aria-label="Invitations">
          {snap.items.map(item => {
            const copy = INVITATION_STATE_COPY[item.state];
            const canRevoke = revocable(item.state);
            const allowed = controller.mayRevoke(item);
            return (
              <li key={item.id} className="invite-row">
                <div className="invite-who">
                  <span className="row-name">{item.email}</span>
                  <span className="meta">{roleText(item.role_ids)}</span>
                </div>
                <div className="invite-state">
                  <span className={`chip ${copy.tone}`}>{copy.label}</span>
                  {canRevoke ? <span className="meta">{copy.hint}</span> : null}
                </div>
                <span className="meta">Created {formatAbsolute(item.created_at)}. {item.state === 'expired' ? 'Expired' : 'Expires'} {formatAbsolute(item.expires_at)}.</span>
                {canRevoke ? (
                  <button type="button" className="btn btn-small" onClick={() => setConfirm(item)} disabled={!allowed || snap.revoking !== null} title={allowed ? undefined : 'Only an owner can revoke an owner invitation.'} aria-busy={snap.revoking === item.id || undefined}>
                    {snap.revoking === item.id ? 'Revoking…' : 'Revoke'}
                  </button>
                ) : <span />}
                {snap.rowMessage?.id === item.id ? <p className="meta invite-message" role="status">{snap.rowMessage.text}</p> : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {snap.next ? (
        <div className="list-foot">
          <button type="button" className="btn" onClick={() => controller.loadMore()} disabled={snap.list !== 'idle'}>{snap.list === 'more' ? 'Loading…' : 'Load more invitations'}</button>
        </div>
      ) : null}
      <Dialog
        open={confirm !== null}
        title="Revoke this invitation?"
        onClose={() => setConfirm(null)}
        footer={<><button type="button" className="btn" onClick={() => setConfirm(null)}>Keep it</button><button type="button" className="btn btn-end" onClick={() => { if (confirm) void controller.revoke(confirm); setConfirm(null); }}>Revoke</button></>}
      >
        <p>{confirm ? `The link sent to ${confirm.email} will stop working. Nothing else changes.` : ''}</p>
      </Dialog>
    </div>
  );
}
